// The corkboard's controls beside the room's tabs, as the Mac's RoomTabHeader has them: the hint ("Part Two · 8 scenes ·
// drag to reorder"), Layout (Grid, Freeform, Swimlanes), Lanes (the field, Line Up by Reading Order, Show Empty
// Lanes) while the swimlanes show, and the Heatmap menu (Revision Heatmap, its period, Mark Scenes Cold After). Each is
// a per-browser choice (the Mac's AppStorage and AppSettings), shared with the page. Outside the story's scenes
// (`planShowsScenes`) there are no swimlanes and no heatmap. While the contents are filtered, Filtered chooses whether
// the cards the filter leaves out are hidden or dimmed in place, and clears the filter.
import { laneFields, planShowsScenes } from '@writing-suite/core/plan';
import { DEFAULT_COLD_DAYS, HEATMAP_PERIODS, heatmapPeriodName } from '@writing-suite/core/revise';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { COLD_CHOICES, LAYOUTS, PREF, useSharedPreference, type CorkboardLayout } from '../../store/plan.ts';
import { boardHint } from '../Corkboard.tsx';
import { MenuButton, type MenuEntry } from '../Menu.tsx';
import { useCorkboardLayout, usePhone } from './hooks.ts';

export function HeatmapMenu() {
  const [on, setOn] = useSharedPreference(PREF.heat, false);
  const [days, setDays] = useSharedPreference(PREF.heatDays, 30);
  const [coldDays, setColdDays] = useSharedPreference(PREF.coldDays, DEFAULT_COLD_DAYS);
  const entries: MenuEntry[] = [
    { kind: 'check', label: 'Revision Heatmap', checked: on, onSelect: () => setOn(!on) },
    { kind: 'heading', label: 'Heatmap Period' },
    ...HEATMAP_PERIODS.map((d): MenuEntry => ({ kind: 'radio', label: heatmapPeriodName(d), checked: d === days, disabled: !on, onSelect: () => setDays(d) })),
    { kind: 'separator' },
    { kind: 'heading', label: 'Mark Scenes Cold After' },
    ...COLD_CHOICES.map((d): MenuEntry => ({ kind: 'radio', label: d === 0 ? 'Never' : `${d} Days`, checked: d === coldDays, onSelect: () => setColdDays(d) })),
  ];
  return (
    <MenuButton label="Heatmap" className={`quiet-button header-menu${on ? ' on' : ''}`} title="Colour scenes by how much they were rewritten; mark scenes you haven’t opened in a while" entries={entries}>
      Heatmap
    </MenuButton>
  );
}

/** The contents' filter on the board: hide the cards it leaves out, or fade them where they are; Clear Filter. */
export function FilteredMenu() {
  const ws = useWorkspace();
  const [dims, setDims] = useSharedPreference(PREF.dimsFilteredOut, false);
  const filter = ws?.filter;
  if (!filter?.view) return null;
  return (
    <MenuButton
      label="Filtered"
      className="quiet-button header-menu on filtered-menu"
      title="The shelf’s filter: hide the cards it leaves out, or fade them"
      entries={[
        { kind: 'radio', label: 'Hide Others', checked: !dims, onSelect: () => setDims(false) },
        { kind: 'radio', label: 'Dim Others', checked: dims, onSelect: () => setDims(true) },
        { kind: 'separator' },
        { label: 'Clear Filter', onSelect: () => filter.clear() },
      ]}
    >
      {dims ? 'Filtered: Dim' : 'Filtered'}
    </MenuButton>
  );
}

export function CorkboardControls(props: PageProps) {
  const ws = useWorkspace();
  const phone = usePhone();
  const { shown: layout, offered, set: setLayout, posts } = useCorkboardLayout(props.project, props.arrangement, props.tree, props.node?.id ?? null);
  const [fieldId, setFieldId] = useSharedPreference<string>(PREF.laneField, 'status');
  const [aligned, setAligned] = useSharedPreference(PREF.lanesAligned, true);
  const [showEmpty, setShowEmpty] = useSharedPreference(PREF.emptyLanes, false);
  const fields = laneFields(props.project.fields());
  const field = fields.find((f) => f.id === fieldId) ?? fields[0];
  const scenes = planShowsScenes(props.arrangement.role);
  const shown: CorkboardLayout = phone ? 'grid' : layout;
  return (
    <div className="plan-controls">
      <span className="plan-hint">{boardHint(props, shown, !!ws?.canStructure)}</span>
      {phone ? null : (
        <MenuButton
          label="Layout"
          className="quiet-button header-menu"
          title={posts ? 'Lay the posts out as a board by status, a grid, or freely' : scenes ? 'Lay the cards out in a grid, freely, or in swimlanes' : 'Lay the cards out in a grid, or freely'}
          entries={offered.map((l) => ({ kind: 'radio', label: l.name, checked: l.id === shown, onSelect: () => setLayout(l.id) }))}
        >
          {LAYOUTS.find((l) => l.id === shown)?.name ?? 'Grid'}
        </MenuButton>
      )}
      {!phone && shown === 'swimlanes' ? (
        <MenuButton
          label="Lanes"
          className="quiet-button header-menu"
          title="Choose the field that makes the lanes"
          entries={[
            ...fields.map((f): MenuEntry => ({ kind: 'radio', label: f.name, checked: f.id === field?.id, onSelect: () => setFieldId(f.id) })),
            { kind: 'separator' },
            { kind: 'check', label: 'Line Up by Reading Order', checked: aligned, onSelect: () => setAligned(!aligned) },
            { kind: 'check', label: 'Show Empty Lanes', checked: showEmpty, onSelect: () => setShowEmpty(!showEmpty) },
          ]}
        >
          Lanes: {field?.name ?? 'None'}
        </MenuButton>
      ) : null}
      {scenes ? <HeatmapMenu /> : null}
      <FilteredMenu />
    </div>
  );
}
